import { Toast } from "@/components/kit";
import MultiStepFormLayout from "@/components/layout/multiStepFormLayout/MultiStepFormLayout";
import OrgDetailStep from "./OrgDetailStep";
import AdminAssignment from "./AdminAssignment";
import { useStoreOrg } from "../../store/useStoreOrganization";
import useCreateOrg from "@/hooks/query/organization/useCreateOrg";
import useStoreMe from "@/hooks/store/useStoreMe";
import useUpdateLogo from "@/hooks/query/organization/useUpdateLogo";
import { Navigate, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import TrioPlanLicense from "./TrioPlanLicense";
import useGetCheckoutLink, {
  TCheckoutLinkPayload,
} from "@/hooks/query/billing/useGetCheckoutLink";
import { OpenNewTab } from "@/utilize/downloader";
import { usePermission } from "@/hooks/usePermission";
import { PERMISSIONS } from "@/constants/permissions";

const AddOrganization = () => {
  const { hasPermission } = usePermission();
  const canCreateOrganization = hasPermission(PERMISSIONS.ORGANIZATION_CREATE);

  const { region, file, country, industry, name, regionDomain, users, reset } =
    useStoreOrg();
  const { me } = useStoreMe();

  const { mutateAsync, isPending } = useCreateOrg();
  const { mutateAsync: updateLogoMutate, isPending: isPendingUpdateLogo } =
    useUpdateLogo();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [createdOrgId, setCreatedOrgId] = useState<string | null>(null);
  const { mutateAsync: getCheckoutLink } = useGetCheckoutLink();
  const [checkoutLinkPayload, setCheckoutLinkPayload] =
    useState<TCheckoutLinkPayload | null>(null);

  useEffect(() => {
    return () => reset();
  }, []);

  const handleStep2Submit = async () => {
    const result = await mutateAsync({
      country,
      industry,
      displayName: name,
      region: regionDomain.toUpperCase(),
      name,
      users: users.filter((item) => item.id !== me?.id),
    });

    if (file && result.data?.id) {
      await updateLogoMutate({
        file,
        organizationId: result.data.id,
      });
    }

    setCreatedOrgId(result.data?.id);

    Toast({
      status: "success",
      title: "Success",
      description: "Organization created successfully.",
    });
  };

  const handleStep3Submit = () => {
    if (!createdOrgId || !checkoutLinkPayload) {
      navigate("/organizations");
      queryClient.invalidateQueries({
        queryKey: ["organization-list"],
      });
      reset();
      return;
    }
    getCheckoutLink(
      { payload: checkoutLinkPayload, organizationId: createdOrgId },
      {
        onSuccess: (data) => {
          Toast({
            status: "success",
            title: "Success",
            description: "Redirecting to checkout page...",
          });
          OpenNewTab(data.url);
          navigate("/organizations");
          queryClient.invalidateQueries({
            queryKey: ["organization-list"],
          });
          reset();
        },
      },
    );
  };

  const steps = [
    {
      title: "Organization details",
      component: <OrgDetailStep />,
      stepNumber: 1,
    },
    {
      title: "Admin assignment",
      component: <AdminAssignment />,
      stepNumber: 2,
      onSubmit: handleStep2Submit,
      finishButtonText: "Finish and view plans",
    },
    {
      title: "Trio plan & license",
      component: (
        <TrioPlanLicense
          organizationId={createdOrgId || ""}
          setCheckoutLinkPayload={setCheckoutLinkPayload}
        />
      ),
      stepNumber: 3,
      onSubmit: handleStep3Submit,
    },
  ];

  const nextIsEnabled = !!region && !!file && !!country && !!industry && !!name;
  const saveIsEnabled = users.length
    ? !!users.length && users.every((item) => item.roleId)
    : true;

  if (!canCreateOrganization) {
    return <Navigate to="/organizations" replace />;
  }

  return (
    <MultiStepFormLayout
      title="Add organization"
      description={
        <div className="flex gap-3 items-center ">
          Set up a new organization to manage users, devices, and admins in one
          place.
        </div>
      }
      saveButtonText="Save"
      steps={steps}
      onSubmit={handleStep3Submit}
      isValid={saveIsEnabled}
      showSaveButton={false}
      isLoading={isPending || isPendingUpdateLogo}
      isNextDisabled={nextIsEnabled}
      onCancel={() => {
        navigate("/organizations");
      }}
      finishButtonText="Proceed to payment"
      showCancelButton={true}
      iconName="building-main"
    />
  );
};

export default AddOrganization;
